import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEmailTemplateById, deleteEmailTemplate, checkTemplateLinkedToScheduled } from "../../Services/EmailTemplate";
import { getEventById } from "../../Services/Event";
import Modal from "../../components/Atomos/Modals/Modal";
import NewButton from "../../components/Atomos/Buttons/newButton";
import CloseModal from "../../components/Atomos/Icons/EditMode/CloseModal";
import { IEmailTemplate } from "../../interfaces/models/IEmailTemplate";
import { EmailTypeEnum } from "../../Enums/EmailEnum";
import { AccessTypeWithStaff } from "../../Enums/UserEnums";
import { useAuth } from "../../store/AuthContext";
import { useDynamicPageMeta } from "../../hooks/useDynamicPageMeta";
import {
  findUnsupportedEmailPlaceholders,
  unsupportedPlaceholderWarningMessage,
} from "../../utils/emailTemplatePlaceholders";
import { toastWarn } from "../../utils/toast";

export default function SeeTemplates(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<IEmailTemplate | null>(null);
  const [eventTitle, setEventTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalMode, setDeleteModalMode] = useState<"confirm" | "cannot" | null>(null);
  const [checkingScheduled, setCheckingScheduled] = useState(false);
  const placeholderWarnedForIdRef = useRef<string | null>(null);
  const { setPageMeta, clearPageMeta } = useDynamicPageMeta();

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      const data = await getEmailTemplateById(id);
      if (!cancelled) {
        setTemplate(data ?? null);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!template?.id || !template.template?.trim()) {
      return;
    }
    if (placeholderWarnedForIdRef.current === template.id) {
      return;
    }
    const unsupported = findUnsupportedEmailPlaceholders(template.template);
    if (unsupported.length === 0) {
      return;
    }
    placeholderWarnedForIdRef.current = template.id;
    toastWarn(unsupportedPlaceholderWarningMessage(unsupported), {
      autoClose: 8000,
    });
  }, [template?.id, template?.template]);

  useEffect(() => {
    const eventId = template?.emailEventId?.trim();
    if (!eventId) {
      setEventTitle(null);
      return;
    }
    let cancelled = false;
    getEventById(eventId).then((event) => {
      if (!cancelled) {
        setEventTitle(event?.title?.trim() ?? null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [template?.emailEventId]);

  useEffect(() => {
    if (!id || !template?.title?.trim()) {
      return;
    }

    setPageMeta({
      breadcrumbLabels: {
        [`/email-design/${id}`]: template.title.trim(),
      },
    });

    return () => {
      clearPageMeta();
    };
  }, [clearPageMeta, id, setPageMeta, template?.title]);

  const openDeleteModal = async () => {
    if (!id) {
      return;
    }
    setCheckingScheduled(true);
    try {
      const linked = await checkTemplateLinkedToScheduled(id);
      setDeleteModalMode(linked ? "cannot" : "confirm");
      setDeleteModalOpen(true);
    } finally {
      setCheckingScheduled(false);
    }
  };
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteModalMode(null);
  };
  const handleViewScheduledEmail = () => {
    closeDeleteModal();
    navigate("/send-email/scheduled-emails");
  };

  const handleConfirmDelete = async () => {
    if (!id) {
      return;
    }
    setDeleting(true);
    try {
      await deleteEmailTemplate(id);
      closeDeleteModal();
      window.location.href = "/email-design";
    } catch {
      setDeleting(false);
    }
  };

  const handleModify = () => {
    if (id && t.templateSource === "beefree") {
      navigate(`/email-design/${id}/edit`);
    } else if (id && t.templateSource === "html") {
      navigate(`/email-design/${id}/edit-html`);
    } else {
      navigate("/email-design");
    }
  };

  const { tokenDecoded } = useAuth();
  const isSuperStaff = tokenDecoded?.extension_WSG_Permissions === AccessTypeWithStaff.wsgSuper;

  const t = template ?? ({} as IEmailTemplate);
  const canManageTemplate = !t.isSystemTemplate || isSuperStaff;
  const emailBody = t.template || "";
  const attachmentUrl = t.attachment && String(t.attachment).trim() !== "" ? String(t.attachment).trim() : "";
  const hasAttachment = attachmentUrl !== "";
  const rawFilename =
    hasAttachment && attachmentUrl.startsWith("http")
      ? attachmentUrl.split("/").filter(Boolean).pop() ?? attachmentUrl
      : attachmentUrl;
  const attachmentTitle =
    rawFilename && /^\d+/.test(rawFilename)
      ? rawFilename.replace(/^\d+/, "").trim() || rawFilename
      : rawFilename;

  if (loading) {
    return (
      <main className="text-left p-6">
        <p className="text-gray-font">Loading...</p>
      </main>
    );
  }

  const deleteConfirmMessage = `Are you sure you want to delete this template ${t.emailNumber ? `Email #${t.emailNumber}` : ""}${t.emailNumber && t.title ? " " : ""}${t.title ? t.title : ""}`.trim() || "Are you sure you want to delete this template?";

  return (
    <main className="text-left p-6 bg-white">
      <Modal
        className="!text-Default py-6 px-6 md:py-8 md:px-8 lg:py-10 lg:px-10"
        isModalOpen={deleteModalOpen}
        toggleModal={closeDeleteModal}
        title={deleteModalMode === "cannot" ? "Cannot Delete Template" : "Delete Template"}
        titleColor="text-Default"
      >
        <div className="flex flex-col gap-6 min-w-[26rem] sm:min-w-[32rem]">
          {deleteModalMode === "cannot" ? (
            <>
              <p className="text-base text-gray-font">
                This template is currently linked to a scheduled email. To delete this template, you must first cancel the scheduled mailing.
              </p>
              <div className="flex flex-col gap-4 w-full sm:flex-row sm:justify-end sm:w-auto">
                <NewButton
                  type="button"
                  onClick={handleViewScheduledEmail}
                  content="text"
                  size="small"
                  style="filled"
                  color="accent"
                  text="View Scheduled Email"
                  className="w-full sm:w-auto"
                />
                <NewButton
                  type="button"
                  onClick={closeDeleteModal}
                  content="text"
                  size="small"
                  style="outlined"
                  color="neutral"
                  text="Got it"
                  className="w-full sm:w-auto"
                />
              </div>
            </>
          ) : (
            <>
              <p className="text-base text-gray-font">{deleteConfirmMessage}</p>
              <div className="flex flex-col gap-4 w-full sm:flex-row sm:justify-end sm:w-auto">
                <NewButton
                  type="button"
                  onClick={closeDeleteModal}
                  content="textIcon"
                  size="small"
                  style="outlined"
                  color="neutral"
                  icon={<CloseModal /> as JSX.Element}
                  text="No, cancel"
                  className="w-full sm:w-auto"
                />
                <NewButton
                  type="button"
                  onClick={handleConfirmDelete}
                  content="textIcon"
                  size="small"
                  style="filled"
                  color="accent"
                  text={deleting ? "Deleting…" : "Yes, delete"}
                  disabled={deleting}
                  className="w-full sm:w-auto"
                />
              </div>
            </>
          )}
        </div>
      </Modal>

      <div className="flex items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl lg:text-3xl font-medium text-Default">Email Information</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => canManageTemplate && handleModify()}
            disabled={!canManageTemplate}
            className="px-4 py-2 bg-Default text-white rounded font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canManageTemplate
                ? "Only WSG Staff Super can modify system templates"
                : undefined
            }
          >
            Modify
          </button>
          <button
            type="button"
            onClick={() =>
              canManageTemplate && isSuperStaff && openDeleteModal()
            }
            disabled={
              deleting || !isSuperStaff || !canManageTemplate || checkingScheduled
            }
            className="px-4 py-2 bg-Default text-white rounded font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canManageTemplate
                ? "Only WSG Staff Super can modify system templates"
                : !isSuperStaff
                  ? "Only WSG Staff Super can delete templates"
                  : undefined
            }
          >
            Delete
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-base lg:text-lg font-semibold text-gray-font">From</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font ">{t.sender ?? "—"}</p>
          </div>
          <div>
            <p className="text-base lg:text-lg font-semibold text-gray-font">From Email</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font">{t.fromEmail ?? "—"}</p>
          </div>
          <div>
            <p className="text-base lg:text-lg font-semibold text-gray-font">Subject</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font">{t.subject ?? "—"}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 min-w-0">
            <p className="text-base lg:text-lg font-semibold text-gray-font">Title</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font break-words">{t.title ?? "—"}</p>
          </div>
          <div className="min-w-0">
            <p className="text-base lg:text-lg font-semibold text-gray-font">Event</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font">{eventTitle ?? "—"}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-base lg:text-lg font-semibold text-gray-font">Category</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font">{t.category ?? "—"}</p>
          </div>
          <div>
            <p className="text-base lg:text-lg font-semibold text-gray-font">Type</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font">{EmailTypeEnum[t.emailType]}</p>
          </div>
          <div>
            <p className="text-base lg:text-lg font-semibold text-gray-font">Email Number</p>
            <p className="text-base lg:text-lg mt-0.5 text-gray-font">{t.emailNumber ?? "—"}</p>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <p className="text-base lg:text-lg font-semibold text-gray-font">Attachments</p>
          <p className="text-base lg:text-lg mt-0.5 text-gray-font">
            {hasAttachment ? (
              attachmentUrl.startsWith("http") ? (
                <a
                  href={attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-Default hover:underline"
                >
                  {attachmentTitle}
                </a>
              ) : (
                attachmentTitle
              )
            ) : (
              "No attachments"
            )}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <p className="text-base lg:text-lg font-semibold text-gray-font mb-2">Email:</p>
        <div
          className="prose max-w-none min-h-[120px] p-4 bg-gray-50 rounded border border-gray-200 overflow-auto"
          dangerouslySetInnerHTML={{
            __html: emailBody || "<p class=\"text-gray-400\">No content</p>",
          }}
        />
      </div>
    </main>
  );
}
